import * as React from 'react';
import {
  AlertTriangle,
  Check,
  CircleDashed,
  Info,
  Link2,
  RefreshCw,
  SendHorizontal,
} from 'lucide-react';
import { Callout, PanelSection, SidePanel, WorkspaceButton } from '@/components/ui/workspace';
import { VendorCitation, VendorConfidentialChip } from '@/components/ui/citation-chip';
import { StatusChip } from '@/components/ui/status-chip';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils/cn';
import {
  searchConfirmedEvidence,
  type MapPainPoint,
  type MapProductRef,
  type MapStepDto,
  type MapStepRef,
  type PickableEvidence,
  type VendorInfoChange,
} from '@/lib/api/process-maps';
import { useKbItems, type KbItemRow } from '@/lib/api/vendor-knowledge';
import { kindLabel } from '../../vendor-knowledge/kb-logic';
import { STEP_TYPE_LABEL, shortDate } from './map-logic';
import { TAG_LABEL, changeText } from './future-logic';

/**
 * The step panel (T-0189; prototype `mapPanel()`): one step's evidence,
 * pain points and confirmation, with linking and unlinking evidence (each a
 * change set from the side panel, applied like any other) and a follow-up
 * for a step nobody has described yet. The details the prototype drew as
 * tags on the task live here (OQ-V).
 */

const LINKISH =
  'text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900 disabled:opacity-60';

export function EngagementStepPanel({
  engagementId,
  areaId,
  step,
  laneName,
  canChange,
  onLink,
  onUnlink,
  painPoints,
  onLinkPainPoint,
  onUnlinkPainPoint,
  onFollowUp,
  onClose,
}: {
  engagementId: string;
  areaId: string;
  step: MapStepDto;
  laneName: string;
  /** Evidence can be linked: the latest version, not in edit mode, nothing else pending. */
  canChange: string | null;
  onLink: (item: PickableEvidence) => void;
  onUnlink: (evidenceId: string, number: string | null) => void;
  /** The engagement's confirmed pain points, to name the step's and to link one. */
  painPoints?: MapPainPoint[];
  /** T-0181 — put a confirmed pain point on the step, or take one off (side-panel change sets). */
  onLinkPainPoint?: (item: MapPainPoint) => void;
  onUnlinkPainPoint?: (painPointId: string) => void;
  onFollowUp: () => void;
  onClose: () => void;
}) {
  const [picking, setPicking] = React.useState(false);
  const [pickingPain, setPickingPain] = React.useState(false);
  const isTask = step.confirmation !== null;
  const painText = new Map((painPoints ?? []).map((p) => [p.id, p.text]));
  const evidence = step.citations.filter((c) => c.kind === 'discovery_evidence');
  const confirmedN = evidence.filter((c) => c.evidence_status === 'confirmed').length;
  const validation = step.citations.find((c) => c.kind === 'client_validation')?.validation;
  const added =
    step.origin === 'chat'
      ? `Added in chat by ${step.origin_user?.name ?? 'a consultant'}. `
      : step.origin === 'manual' || step.origin === 'canvas'
        ? `Added by hand by ${step.origin_user?.name ?? 'a consultant'}. `
        : '';

  return (
    <SidePanel
      aria-label={`Step: ${step.name}`}
      path={`${laneName} · ${STEP_TYPE_LABEL[step.type]}`}
      onClose={onClose}
    >
      <div className="text-body font-semibold text-ink-900">{step.name || 'Unnamed'}</div>

      {validation ? (
        <Callout tone="ok" icon={Check}>
          Confirmed by {validation.validated_by ?? 'the client'} when validating the map
          {validation.validated_at ? ` on ${shortDate(validation.validated_at)}` : ''}. It cites
          that validation.
        </Callout>
      ) : step.confirmation === 'unconfirmed' && !step.needs_attention ? (
        <Callout tone="tip" icon={Info}>
          {added}Nobody has described this step yet. Confirm it in a session or a follow-up, or link
          evidence, before the map is final.
        </Callout>
      ) : null}
      {step.needs_attention ? (
        <Callout tone="warn" icon={AlertTriangle}>
          No confirmed evidence left: what this step cited was withdrawn. Link other evidence, ask a
          follow-up, or remove the step. The map does not change until you do.
        </Callout>
      ) : null}

      {step.system ? (
        <PanelSection label="Done in">
          <div className="text-body-sm text-ink-900">{step.system}</div>
        </PanelSection>
      ) : null}

      {isTask ? (
        <PanelSection label={`Evidence (${confirmedN})`}>
          {evidence.length === 0 ? <p className="text-meta text-ink-500">None yet.</p> : null}
          <ul className="flex flex-col gap-2">
            {evidence.map((c) => {
              const gone = c.evidence_status !== 'confirmed';
              return (
                <li
                  key={c.evidence_id}
                  className={cn(
                    'rounded-card border border-ink-100 px-3 py-2',
                    gone && 'bg-paper-50',
                  )}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <span className="font-mono text-mono-sm font-medium text-ink-900">
                      {c.evidence_number ?? 'Evidence'}
                    </span>
                    {gone ? (
                      <span className="text-meta text-ink-500">{c.evidence_status}</span>
                    ) : null}
                    <span className="flex-1" />
                    <button
                      type="button"
                      className={LINKISH}
                      disabled={canChange !== null}
                      title={canChange ?? undefined}
                      onClick={() => onUnlink(c.evidence_id!, c.evidence_number ?? null)}
                    >
                      Unlink
                    </button>
                  </div>
                  <p
                    className={cn(
                      'font-serif text-body-sm text-ink-700',
                      gone && 'text-ink-500 line-through',
                    )}
                  >
                    {c.text ?? ''}
                  </p>
                </li>
              );
            })}
          </ul>
          {picking ? (
            <EvidencePicker
              engagementId={engagementId}
              areaId={areaId}
              exclude={evidence.map((c) => c.evidence_id ?? '')}
              onPick={(item) => {
                setPicking(false);
                onLink(item);
              }}
              onCancel={() => setPicking(false)}
            />
          ) : (
            <div className="mt-2">
              <WorkspaceButton
                disabled={canChange !== null}
                title={canChange ?? undefined}
                onClick={() => setPicking(true)}
              >
                <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                Link evidence
              </WorkspaceButton>
            </div>
          )}
        </PanelSection>
      ) : null}

      {isTask && (step.pain_points.length > 0 || onLinkPainPoint) ? (
        <PanelSection label="Pain points here">
          {step.pain_points.length === 0 ? <p className="text-meta text-ink-500">None.</p> : null}
          <ul className="flex flex-col gap-1.5">
            {step.pain_points.map((p) => (
              <li key={p.id} className="flex items-start gap-2">
                <span className="inline-flex h-5 flex-none items-center rounded-full bg-posture-critical px-2 font-mono text-[11px] font-medium text-paper-0">
                  {p.number ?? 'Pain point'}
                </span>
                <span className="min-w-0 flex-1 text-body-sm text-ink-700">
                  {painText.get(p.id) ?? ''}
                </span>
                {onUnlinkPainPoint ? (
                  <button
                    type="button"
                    className={LINKISH}
                    disabled={canChange !== null}
                    title={canChange ?? undefined}
                    aria-label={`Unlink ${p.number ?? 'the pain point'}`}
                    onClick={() => onUnlinkPainPoint(p.id)}
                  >
                    Unlink
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {onLinkPainPoint ? (
            pickingPain ? (
              <PainPointPicker
                items={(painPoints ?? []).filter(
                  (pp) => !step.pain_points.some((x) => x.id === pp.id),
                )}
                areaId={areaId}
                onPick={(item) => {
                  setPickingPain(false);
                  onLinkPainPoint(item);
                }}
                onCancel={() => setPickingPain(false)}
              />
            ) : (
              <div className="mt-2">
                <WorkspaceButton
                  disabled={canChange !== null}
                  title={canChange ?? undefined}
                  onClick={() => setPickingPain(true)}
                >
                  <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Link a pain point
                </WorkspaceButton>
              </div>
            )
          ) : null}
        </PanelSection>
      ) : null}

      {step.confirmation === 'unconfirmed' ? (
        <div>
          <WorkspaceButton onClick={onFollowUp}>
            <SendHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
            Ask a follow-up
          </WorkspaceButton>
        </div>
      ) : null}
    </SidePanel>
  );
}

function EvidencePicker({
  engagementId,
  areaId,
  exclude,
  onPick,
  onCancel,
}: {
  engagementId: string;
  areaId: string;
  exclude: string[];
  onPick: (item: PickableEvidence) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = React.useState('');
  const [anyArea, setAnyArea] = React.useState(false);
  const [items, setItems] = React.useState<PickableEvidence[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      searchConfirmedEvidence(engagementId, { area_id: anyArea ? undefined : areaId, q }).then(
        (found) => {
          if (!alive) return;
          setItems(found);
          setError(null);
        },
        (e: unknown) => {
          if (alive) setError(e instanceof Error ? e.message : 'The evidence could not be loaded.');
        },
      );
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [engagementId, areaId, anyArea, q]);

  const shown = (items ?? []).filter((i) => !exclude.includes(i.id));
  return (
    <div className="mt-2 flex flex-col gap-2 rounded-card border border-ink-100 p-2.5">
      <input
        aria-label="Search confirmed evidence"
        placeholder="Search confirmed evidence"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="h-8 rounded-card border border-ink-200 px-2.5 text-body-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
      />
      <label className="flex items-center gap-2 text-meta text-ink-700">
        <input type="checkbox" checked={anyArea} onChange={(e) => setAnyArea(e.target.checked)} />
        Any area, not only this process&rsquo;s area
      </label>
      {error ? <p className="text-meta text-posture-critical">{error}</p> : null}
      {items === null && !error ? (
        <div className="flex items-center gap-2 text-meta text-ink-500">
          <Spinner /> Loading…
        </div>
      ) : null}
      {items !== null && shown.length === 0 ? (
        <p className="text-meta text-ink-500">No confirmed evidence found.</p>
      ) : null}
      <ul className="flex max-h-[260px] flex-col gap-1.5 overflow-y-auto">
        {shown.map((item) => (
          <li key={item.id} className="flex items-start gap-2">
            <span className="flex-none font-mono text-mono-sm font-medium text-ink-900">
              {item.number ?? '—'}
            </span>
            <span className="min-w-0 flex-1 text-meta text-ink-700">{item.text}</span>
            <WorkspaceButton variant="secondary" onClick={() => onPick(item)}>
              Link
            </WorkspaceButton>
          </li>
        ))}
      </ul>
      <button type="button" className={cn(LINKISH, 'self-start')} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/** Confirmed pain points to put on a step: this process's area first, or any area. */
function PainPointPicker({
  items,
  areaId,
  onPick,
  onCancel,
}: {
  items: MapPainPoint[];
  areaId: string;
  onPick: (item: MapPainPoint) => void;
  onCancel: () => void;
}) {
  const [anyArea, setAnyArea] = React.useState(false);
  const shown = anyArea ? items : items.filter((p) => p.area_ids.includes(areaId));
  return (
    <div className="mt-2 flex flex-col gap-2 rounded-card border border-ink-100 p-2.5">
      <label className="flex items-center gap-2 text-meta text-ink-700">
        <input type="checkbox" checked={anyArea} onChange={(e) => setAnyArea(e.target.checked)} />
        Any area, not only this process&rsquo;s area
      </label>
      {shown.length === 0 ? (
        <p className="text-meta text-ink-500">No confirmed pain points to link.</p>
      ) : null}
      <ul className="flex max-h-[260px] flex-col gap-1.5 overflow-y-auto">
        {shown.map((item) => (
          <li key={item.id} className="flex items-start gap-2">
            <span className="flex-none font-mono text-mono-sm font-medium text-ink-900">
              {item.number ?? '—'}
            </span>
            <span className="min-w-0 flex-1 text-meta text-ink-700">{item.text}</span>
            <WorkspaceButton variant="secondary" onClick={() => onPick(item)}>
              Link
            </WorkspaceButton>
          </li>
        ))}
      </ul>
      <button type="button" className={cn(LINKISH, 'self-start')} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/** A reference process's step: it cites nothing (prototype `mapPanel()`, reference branch). */
export function ReferenceStepPanel({
  name,
  path,
  provenance,
  editing,
  onClose,
}: {
  name: string;
  path: string;
  provenance: string | null;
  editing: boolean;
  onClose: () => void;
}) {
  return (
    <SidePanel aria-label={`Step: ${name}`} path={path} onClose={onClose}>
      <div className="text-body font-semibold text-ink-900">{name || 'Unnamed'}</div>
      <p className="text-body-sm text-ink-700">
        A reference step. When AI drafts an engagement&rsquo;s map, it keeps this step where
        evidence describes it; otherwise the step shows as unconfirmed until someone describes it or
        the client confirms it.
      </p>
      {provenance ? <p className="text-meta text-ink-500">{provenance}</p> : null}
      <p className="text-meta text-ink-500">
        {editing
          ? 'Rename it by double-clicking it on the map in edit mode, or ask the chat.'
          : 'Select Edit to change it in a draft.'}
      </p>
    </SidePanel>
  );
}

// ---------------------------------------------------------------------------
// Future-state steps (T-0208, E13-F09; prototype `mapPanel()`, future branch)
// ---------------------------------------------------------------------------

/** The step's tag, workaround and assumption, as the server derives them. */
export function FutureStepChips({
  step,
}: {
  step: Pick<MapStepDto, 'tag' | 'workaround' | 'is_assumption'>;
}) {
  const tone = step.tag === 'changed' ? 'amber' : step.tag === 'unchanged' ? 'neutral' : 'healthy';
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {step.tag ? (
        <StatusChip tone={tone} size="sm">
          {TAG_LABEL[step.tag]}
        </StatusChip>
      ) : null}
      {step.workaround ? (
        <StatusChip tone="critical" size="sm">
          Workaround
        </StatusChip>
      ) : null}
      {step.is_assumption ? (
        <StatusChip tone="unset" size="sm">
          Assumption
        </StatusChip>
      ) : null}
    </span>
  );
}

/**
 * A future step: what it is against today, the requirements it meets, and
 * the vendor's evidence, each citation exactly as the knowledge base
 * renders it for a consultant (rule 21), or the assumption mark when no
 * evidence is cited (rule 24). Linking, unlinking and marking an assumption
 * are change sets, applied like any other.
 */
export function FutureStepPanel({
  step,
  laneName,
  today,
  product,
  changes,
  canChange,
  onLinkItem,
  onUnlinkItem,
  onMarkAssumption,
  onClose,
}: {
  step: MapStepDto;
  laneName: string;
  /** The current-state step with the same key, when today has one. */
  today: {
    name: string;
    laneName: string;
    type: MapStepDto['type'];
    system: string | null;
  } | null;
  product: MapProductRef;
  /** Newer vendor information on what this step cites. */
  changes: VendorInfoChange[];
  canChange: string | null;
  onLinkItem: (item: KbItemRow) => void;
  onUnlinkItem: (itemNumber: string) => void;
  onMarkAssumption: () => void;
  onClose: () => void;
}) {
  const [picking, setPicking] = React.useState(false);
  const isTask = !['start_event', 'end_event', 'exclusive_gateway'].includes(step.type);
  const cited = step.vendor_citations ?? [];
  const met = step.requirements_met ?? [];

  return (
    <SidePanel
      aria-label={`Step: ${step.name}`}
      path={`${laneName} · ${STEP_TYPE_LABEL[step.type]}`}
      onClose={onClose}
    >
      <div className="flex flex-col gap-1.5">
        <FutureStepChips step={step} />
        <div className="text-body font-semibold text-ink-900">{step.name || 'Unnamed'}</div>
      </div>

      <PanelSection label="Today">
        {step.tag === 'new' ? (
          <p className="text-meta text-ink-500">Not done today.</p>
        ) : step.tag === 'unchanged' ? (
          <p className="text-meta text-ink-500">Done the same way today.</p>
        ) : !today ? (
          <p className="text-meta text-ink-500">
            <Spinner /> Loading today&rsquo;s step…
          </p>
        ) : (
          <div className="rounded-card border border-ink-100 bg-paper-50 px-3 py-2 text-body-sm text-ink-900">
            {today.name}
            <div className="text-meta text-ink-500">
              {today.laneName} · {STEP_TYPE_LABEL[today.type]}
              {today.system ? ` · in ${today.system}` : ''}
            </div>
          </div>
        )}
      </PanelSection>

      {step.system ? (
        <PanelSection label="Done in">
          <div className="text-body-sm text-ink-900">{step.system}</div>
        </PanelSection>
      ) : null}

      {step.workaround ? (
        <Callout tone="warn" icon={AlertTriangle}>
          {product.name} can&rsquo;t do this as the requirement asks, so a workaround stays in the
          process.
        </Callout>
      ) : null}
      {step.is_assumption ? (
        <Callout tone="tip" icon={CircleDashed}>
          An assumption: no vendor evidence says {product.name} does this. A new, changed or
          automated step that cites nothing is marked an assumption automatically. Link a knowledge
          item, or keep it as an assumption.
        </Callout>
      ) : null}
      {changes.length > 0 ? (
        <Callout tone="warn" icon={RefreshCw}>
          Newer vendor information. {changes.map(changeText).join(' ')} The map keeps citing what it
          cites until you redraft the affected steps and apply the change.
        </Callout>
      ) : null}

      {met.length > 0 ? (
        <PanelSection label="Requirements it meets">
          <ul className="flex flex-col gap-1.5">
            {met.map((r) => (
              <li key={r.requirement_id} className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-mono-sm font-medium text-ink-900">{r.number}</span>
                {r.edited_after_load ? (
                  <span className="text-meta text-ink-500">
                    The vendor answered the library wording.
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </PanelSection>
      ) : null}

      {isTask ? (
        <PanelSection label={`Vendor evidence (${cited.length})`}>
          {cited.length === 0 ? (
            <p className="text-meta text-ink-500">None cited.</p>
          ) : (
            <ul className="flex flex-col gap-2.5">
              {cited.map((c) => (
                <li key={c.item_id} className="rounded-card border border-ink-100 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-mono-sm font-medium text-ink-900">
                      {c.number ?? 'Item'}
                    </span>
                    <span className="text-meta text-ink-500">{kindLabel(c.kind)}</span>
                    {c.state !== 'confirmed' ? (
                      <StatusChip tone="at-risk" size="sm">
                        {c.state.charAt(0).toUpperCase() + c.state.slice(1)}
                      </StatusChip>
                    ) : null}
                    {c.citation?.parts.confidential ? <VendorConfidentialChip /> : null}
                    <span className="flex-1" />
                    {c.number ? (
                      <button
                        type="button"
                        className={LINKISH}
                        disabled={canChange !== null}
                        title={canChange ?? undefined}
                        aria-label={`Unlink ${c.number}`}
                        onClick={() => onUnlinkItem(c.number!)}
                      >
                        Unlink
                      </button>
                    ) : null}
                  </div>
                  {c.statement ? (
                    <p className="mt-1 text-body-sm text-ink-900">{c.statement}</p>
                  ) : null}
                  {c.citation ? <VendorCitation citation={c.citation} /> : null}
                </li>
              ))}
            </ul>
          )}
          {picking ? (
            <VendorItemPicker
              product={product}
              exclude={cited.map((c) => c.item_id)}
              onPick={(item) => {
                setPicking(false);
                onLinkItem(item);
              }}
              onCancel={() => setPicking(false)}
            />
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              <WorkspaceButton
                disabled={canChange !== null}
                title={canChange ?? undefined}
                onClick={() => setPicking(true)}
              >
                <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                Link vendor evidence
              </WorkspaceButton>
              {!step.is_assumption ? (
                <WorkspaceButton
                  disabled={canChange !== null}
                  title={canChange ?? undefined}
                  onClick={onMarkAssumption}
                >
                  <CircleDashed className="h-3.5 w-3.5" aria-hidden="true" />
                  Mark as assumption
                </WorkspaceButton>
              ) : null}
            </div>
          )}
        </PanelSection>
      ) : null}

      {step.origin === 'ai_draft' ? (
        <p className="text-meta text-ink-500">
          Drafted by AI from the validated current state, the requirements and {product.name}
          &rsquo;s confirmed knowledge items.
        </p>
      ) : null}
    </SidePanel>
  );
}

/** A current step the future state no longer has (Compare with today). */
export function RemovedStepPanel({
  step,
  product,
  onClose,
}: {
  step: MapStepRef;
  product: MapProductRef;
  onClose: () => void;
}) {
  return (
    <SidePanel
      aria-label={`Step: ${step.name}`}
      path={`Today · ${step.lane_name} · ${STEP_TYPE_LABEL[step.type]}`}
      onClose={onClose}
    >
      <StatusChip tone="neutral" size="sm" className="self-start">
        Removed
      </StatusChip>
      <div className="text-body font-semibold text-ink-900">{step.name || 'Unnamed'}</div>
      <p className="text-body-sm text-ink-700">
        Done today, and not in the future state with {product.name}: the product removes or replaces
        it.
      </p>
    </SidePanel>
  );
}

/**
 * Knowledge items a future step may cite: confirmed, of the map's product
 * or (company items) its vendor (rule 10). The server checks the link again.
 */
function VendorItemPicker({
  product,
  exclude,
  onPick,
  onCancel,
}: {
  product: MapProductRef;
  exclude: string[];
  onPick: (item: KbItemRow) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = React.useState('');
  const [query, setQuery] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const items = useKbItems({
    vendor_id: product.vendor.id,
    status: 'confirmed',
    ...(query ? { q: query } : {}),
  });
  const shown = (items.data?.pages.flatMap((p) => p.items) ?? []).filter(
    (i) =>
      i.number !== null &&
      !exclude.includes(i.id) &&
      (i.product === null || i.product.id === product.id),
  );
  return (
    <div className="mt-2 flex flex-col gap-2 rounded-card border border-ink-100 p-2.5">
      <input
        aria-label={`Search ${product.name}'s knowledge items`}
        placeholder={`Search ${product.name}'s confirmed items`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="h-8 rounded-card border border-ink-200 px-2.5 text-body-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
      />
      {items.isError ? (
        <p className="text-meta text-posture-critical">The knowledge items could not be loaded.</p>
      ) : null}
      {items.isPending ? (
        <div className="flex items-center gap-2 text-meta text-ink-500">
          <Spinner /> Loading…
        </div>
      ) : null}
      {items.data && shown.length === 0 ? (
        <p className="text-meta text-ink-500">No confirmed items found.</p>
      ) : null}
      <ul className="flex max-h-[260px] flex-col gap-1.5 overflow-y-auto">
        {shown.map((item) => (
          <li key={item.id} className="flex items-start gap-2">
            <span className="flex-none font-mono text-mono-sm font-medium text-ink-900">
              {item.number}
            </span>
            <span className="min-w-0 flex-1 text-meta text-ink-700">
              {item.statement ?? item.excerpt}
              <span className="block text-ink-500">
                {kindLabel(item.kind)}
                {item.product ? '' : ` · ${product.vendor.name} company item`}
              </span>
            </span>
            <WorkspaceButton variant="secondary" onClick={() => onPick(item)}>
              Link
            </WorkspaceButton>
          </li>
        ))}
      </ul>
      <button type="button" className={cn(LINKISH, 'self-start')} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}
